import React, {useEffect, useRef, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from '@fluentui/react';
import {
    useAddWorkspaceMutation,
    useGetInstalledExtensionsQuery,
    useLazyGetCompanyQuery,
    useLazyGetDirectImportPreviewQuery,
    useLazyGetExportExtensionOptionFriendlyNameQuery,
    useLazyGetWorkspaceQuery,
    useRunDirectImportExtensionMutation
} from '../store/Api';
import {useLazyGetWorkspaceRecordLinksQuery} from '../store/endpoints/RecordLinksEndpoints';
import {
    useLazyGetGlobalStorefrontSettingsQuery,
    useLazyGetStorefrontsQuery,
    usePatchGlobalStorefrontSettingsMutation
} from '../store/endpoints/StorefrontSettingsEndpoints';
import {usePatchPreparedCartMutation} from '../store/endpoints/PreparedCartEndpoints';
import {IInstalledExtension} from '../models/Extension';

interface IQuickOpportunityImportParams {
    extensionId: string;
}

/**
 * The PSA quote-link quick flow: a link ConnectWise PSA opens with an opportunity id substituted in,
 * which imports that opportunity into a fresh workspace with every behavior on (items, bundles,
 * contact), seeds a prepared cart (storefront + customer + contact), and lands on the cart's deliver
 * step. One click in the PSA → a cart ready to send. Re-clicking a link whose opportunity was
 * already imported just opens the existing workspace.
 */
const QuickOpportunityImportPage: React.FC<RouteComponentProps<IQuickOpportunityImportParams>> = (props) => {
    const search = new URLSearchParams(props.location.search);
    // PSA quote-link setups vary in the parameter name they append; accept the common shapes.
    const opportunityId = search.get('opp') ?? search.get('recid') ?? search.get('opportunityid') ?? search.get('id') ?? '';
    // Optional: the PSA quote to import when the opportunity has several (Halo's Quotation button passes
    // its quote reference). Without it the extension imports the opportunity's primary or latest quote.
    const quoteId = search.get('quote')?.trim() ?? '';
    const quoteFilter = quoteId !== '' && !/^[{[<]/.test(quoteId) ? {filterField: 'quote', filterValue: quoteId} : {};
    const extensionId = props.match.params.extensionId;

    const installed = useGetInstalledExtensionsQuery();
    const [getRecordLinks] = useLazyGetWorkspaceRecordLinksQuery();
    const [getFriendlyName] = useLazyGetExportExtensionOptionFriendlyNameQuery();
    const [addWorkspace] = useAddWorkspaceMutation();
    const [getPreview] = useLazyGetDirectImportPreviewQuery();
    const [runImport] = useRunDirectImportExtensionMutation();
    const [getWorkspace] = useLazyGetWorkspaceQuery();
    const [getCompany] = useLazyGetCompanyQuery();
    const [getGlobalSettings] = useLazyGetGlobalStorefrontSettingsQuery();
    const [patchGlobalSettings] = usePatchGlobalStorefrontSettingsMutation();
    const [getStorefronts] = useLazyGetStorefrontsQuery();
    const [patchPreparedCart] = usePatchPreparedCartMutation();

    const [status, setStatus] = useState<string>('Preparing…');
    const [error, setError] = useState<string>();
    const [storefrontOptions, setStorefrontOptions] = useState<IDropdownOption[]>();
    const [selectedStorefront, setSelectedStorefront] = useState<string>();
    const [showStorefrontPicker, setShowStorefrontPicker] = useState<boolean>(false);
    const [offerStorefrontSetup, setOfferStorefrontSetup] = useState<boolean>(false);
    const started = useRef(false);
    // Carries the built workspace across the storefront picker pause.
    const pendingRef = useRef<{workspaceId: string; companyId?: string; contactId?: string; name: string}>();

    const finishFlow = async (storefrontId: string) => {
        const pending = pendingRef.current;
        if (!pending) return;
        try {
            setShowStorefrontPicker(false);
            setStatus('Preparing the cart…');
            // The prepared-cart PATCH upserts, so this creates the cart already pointed at the
            // storefront, customer company, and contact — the deliver step's prerequisites.
            await patchPreparedCart({
                id: pending.workspaceId,
                request: {
                    storefrontId,
                    targetCompany: pending.companyId,
                    targetUsers: pending.contactId ? [pending.contactId] : undefined,
                    name: pending.name
                }
            }).unwrap();
            props.history.replace(`/Workspace/${pending.workspaceId}?showPreparedCart&focusDeliver`);
        } catch {
            // The workspace is built even if cart seeding failed — land there rather than dead-ending.
            props.history.replace(`/Workspace/${pending.workspaceId}?showPreparedCart&focusDeliver`);
        }
    };

    const runFlow = async (extension: IInstalledExtension) => {
        try {
            setStatus('Checking for an existing workspace…');
            const links = await getRecordLinks({field: 'opportunityId', externalIds: [opportunityId]})
                .unwrap().catch(() => []);
            const existing = links.find(l => l.externalId === opportunityId);
            if (existing) {
                // Already imported: just open the workspace — no re-import, no forced cart popup.
                props.history.replace(`/Workspace/${existing.workspaceId}`);
                return;
            }

            setStatus('Loading the opportunity from the PSA…');
            let opportunityName = '';
            try {
                opportunityName = await getFriendlyName({
                    extensionId: extension.id, field: 'opportunities', search: opportunityId
                }).unwrap() ?? '';
            } catch { /* fall back to an id-based name */ }

            const previewField = extension.additionalData?.find(f => f.fieldType === 'directImportPreview')?.field;
            if (!previewField) throw new Error('This extension does not support direct import.');
            const preview = await getPreview({
                extensionId: extension.id,
                fieldName: previewField,
                request: {sourceName: extension.source, objectId: opportunityId, page: 1, pageSize: 1000, ...quoteFilter}
            }).unwrap();
            if (preview.success === false) throw new Error(preview.error ?? 'The PSA lookup failed.');
            const items = preview.items ?? [];
            if (items.length === 0) throw new Error('The opportunity has no products to import.');

            setStatus('Creating the workspace…');
            const workspaceName = opportunityName.trim() !== '' ? opportunityName.trim() : `Opportunity ${opportunityId}`;
            const workspace = await addWorkspace(workspaceName).unwrap();
            if (!workspace.id) throw new Error('Failed to create a workspace.');

            setStatus(`Importing ${items.length} item${items.length === 1 ? '' : 's'}…`);
            const result = await runImport({
                extensionId: extension.id,
                request: {
                    catalog: extension.targetCatalog ?? '',
                    workspaceId: workspace.id,
                    items,
                    importBundles: true,
                    attachContact: true,
                    // One click should leave the catalog true to the quote: unmatched lines become products.
                    createMissingProducts: true
                }
            }).unwrap();
            if (result.success === false) throw new Error(result.error ?? 'The import failed.');

            setStatus('Setting up the prepared cart…');
            const workspaceDetails = await getWorkspace(workspace.id).unwrap().catch(() => undefined);
            pendingRef.current = {
                workspaceId: workspace.id,
                companyId: workspaceDetails?.companyId,
                contactId: workspaceDetails?.contactId,
                name: workspaceName
            };

            // Storefront: the customer company's assignment wins, then the org default; with neither
            // we stop and ask — and remember the answer as the default.
            let storefrontId: string | undefined;
            if (workspaceDetails?.companyId) {
                const company = await getCompany(workspaceDetails.companyId).unwrap().catch(() => undefined);
                storefrontId = company?.storefront || undefined;
            }
            if (!storefrontId) {
                const settings = await getGlobalSettings().unwrap().catch(() => undefined);
                storefrontId = settings?.defaultPreparedCartStorefront || undefined;
            }
            if (!storefrontId) {
                const storefronts = await getStorefronts().unwrap().catch(() => undefined);
                const options: IDropdownOption[] = storefronts?.items
                    ?.filter((s) => !!s.id)
                    .map((s) => ({key: s.id as string, text: s.name ?? s.id as string})) ?? [];
                if (options.length === 0) {
                    setError('No storefronts exist yet — a prepared cart needs a storefront to deliver from.');
                    setOfferStorefrontSetup(true);
                    return;
                }
                if (options.length === 1) {
                    void patchGlobalSettings({defaultPreparedCartStorefront: options[0].key as string});
                    await finishFlow(options[0].key as string);
                    return;
                }
                setStorefrontOptions(options);
                setShowStorefrontPicker(true);
                return;
            }
            await finishFlow(storefrontId);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'The import failed.');
        }
    };

    useEffect(() => {
        if (started.current || !installed.data) return;
        if (!opportunityId) {
            setError('No opportunity id was provided in the link. Check the Quote Link setup in your PSA.');
            return;
        }
        // A literal substitution tag (ConnectWise "{recid}", Autotask "<OPPORTUNITYID>") means the link
        // was opened outside the PSA, or the PSA didn't recognize the tag.
        if (opportunityId.startsWith('{') || opportunityId.startsWith('[') || opportunityId.startsWith('<')) {
            setError('The opportunity id tag in the link was not replaced. Open this link from an opportunity inside your PSA.');
            return;
        }
        const extension = installed.data.find(e => e.id === extensionId);
        if (!extension) {
            setError('The opportunity import extension in this link is not installed for this organization.');
            return;
        }
        started.current = true;
        void runFlow(extension);
    }, [installed.data]);

    return (
        <div className='main-content'>
            <Stack horizontalAlign='center' tokens={{childrenGap: '1em'}} styles={{root: {marginTop: '6em'}}}>
                {error && <MessageBar messageBarType={MessageBarType.error} styles={{root: {maxWidth: 480}}}>{error}</MessageBar>}
                {error && offerStorefrontSetup &&
                    <PrimaryButton
                        text='Set Up a Storefront'
                        iconProps={{iconName: 'Shop'}}
                        onClick={() => props.history.push('/settings/storefront/storefronts')}
                    />}
                {!error && showStorefrontPicker &&
                    <Stack tokens={{childrenGap: '1em'}} styles={{root: {maxWidth: 440}}}>
                        <Text variant='xLarge'>Choose a storefront</Text>
                        <Text>
                            This customer has no storefront assigned and no default is set yet. Pick the storefront
                            prepared carts should use — it will be saved as the default for future imports.
                        </Text>
                        <Dropdown
                            placeholder='Select a storefront'
                            options={storefrontOptions ?? []}
                            selectedKey={selectedStorefront}
                            onChange={(_, option) => setSelectedStorefront(option?.key as string)}
                        />
                        <PrimaryButton
                            text='Save Default & Continue'
                            disabled={!selectedStorefront}
                            onClick={() => {
                                if (!selectedStorefront) return;
                                void patchGlobalSettings({defaultPreparedCartStorefront: selectedStorefront});
                                void finishFlow(selectedStorefront);
                            }}
                        />
                    </Stack>}
                {!error && !showStorefrontPicker && <Spinner size={SpinnerSize.large} label={status} />}
            </Stack>
        </div>
    );
};

export default QuickOpportunityImportPage;
